import { getToken } from "@/utils/cache/cookies"

const OBSERVED_SCOPE_SESSION_STORAGE_KEY = "business_context_observed_scope_v1"

interface PersistedObservedScope {
  scopeId: string
  tokenFingerprint: string
}

function createTokenFingerprint(token: string) {
  let firstHash = 0x811C9DC5
  let secondHash = 0x9E3779B9
  for (let index = 0; index < token.length; index += 1) {
    const code = token.charCodeAt(index)
    firstHash = Math.imul(firstHash ^ code, 0x01000193)
    secondHash = Math.imul(secondHash ^ code, 0x85EBCA6B)
  }
  return `${token.length}:${(firstHash >>> 0).toString(36)}:${(secondHash >>> 0).toString(36)}`
}

function removePersistedObservedScope() {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(OBSERVED_SCOPE_SESSION_STORAGE_KEY)
    }
  } catch {
    // The in-memory proof still works when sessionStorage is unavailable.
  }
}

function readPersistedObservedScope(token: string): PersistedObservedScope | undefined {
  if (!token) return undefined
  try {
    if (typeof sessionStorage === "undefined") return undefined
    const raw = sessionStorage.getItem(OBSERVED_SCOPE_SESSION_STORAGE_KEY)
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as Partial<PersistedObservedScope>
    if (
      typeof parsed.scopeId === "string"
      && parsed.scopeId
      && parsed.tokenFingerprint === createTokenFingerprint(token)
    ) {
      return parsed as PersistedObservedScope
    }
    removePersistedObservedScope()
  } catch {
    removePersistedObservedScope()
  }
}

function persistObservedScope(scopeId: string, token: string) {
  try {
    if (typeof sessionStorage === "undefined") return
    sessionStorage.setItem(OBSERVED_SCOPE_SESSION_STORAGE_KEY, JSON.stringify({
      scopeId,
      tokenFingerprint: createTokenFingerprint(token),
    } satisfies PersistedObservedScope))
  } catch {
    // F5 recovery is an optimization; an unavailable sessionStorage must not
    // prevent the scoped token from working in the current runtime.
  }
}

const initialAuthToken = getToken() ?? ""
const restoredObservedScope = readPersistedObservedScope(initialAuthToken)
let observedAuthToken = initialAuthToken
let observedScopeId = restoredObservedScope?.scopeId ?? ""
let observedScopeToken = restoredObservedScope ? initialAuthToken : ""

function resetObservedScopeToken() {
  observedScopeId = ""
  observedScopeToken = ""
  removePersistedObservedScope()
}

export function markObservedAuthToken(token = getToken() ?? "") {
  observedAuthToken = token
  if (!token || observedScopeToken !== token) {
    resetObservedScopeToken()
  }
}

export function markObservedScopeToken(scopeId: string, token = getToken() ?? "") {
  if (!scopeId || !token) {
    resetObservedScopeToken()
    return
  }
  observedScopeId = scopeId
  observedScopeToken = token
  persistObservedScope(scopeId, token)
}

export function clearObservedScopeToken(
  expectedScopeId?: string,
  expectedToken?: string,
) {
  if (expectedScopeId !== undefined && observedScopeId !== expectedScopeId) return false
  if (expectedToken !== undefined && observedScopeToken !== expectedToken) return false
  resetObservedScopeToken()
  return true
}

export function isObservedScopeToken(scopeId: string, token = getToken() ?? "") {
  return Boolean(scopeId && token && observedScopeId === scopeId && observedScopeToken === token)
}

export function consumeExternalAuthTokenChange(token = getToken() ?? "") {
  if (!observedAuthToken) {
    observedAuthToken = token
    if (observedScopeToken !== token) {
      resetObservedScopeToken()
    }
    return false
  }
  if (observedAuthToken === token) {
    return false
  }
  observedAuthToken = token
  if (observedScopeToken !== token) {
    resetObservedScopeToken()
  }
  return true
}
